import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, useApiQuery } from "@/api/useApi";
import type {
  Catalog,
  CurrencyRate,
  ExpenseCategoryDefinition,
  ExpenseTypeDefinition,
  ExtraField,
  RuleMode,
} from "@/api/types";
import { Icon } from "@/components/Icon";
import { Sheet } from "@/components/Sheet";
import { money } from "@/format";

const Modes: { value: RuleMode; label: string }[] = [
  { value: "Off", label: "Off" },
  { value: "Warn", label: "Warn" },
  { value: "Block", label: "Enforce" },
];

const Fields: { value: ExtraField; label: string }[] = [
  { value: "None", label: "Nothing extra" },
  { value: "Attendees", label: "Who else was there" },
  { value: "ClientName", label: "Client or vendor name" },
  { value: "Brand", label: "Brand" },
  { value: "Other", label: "Other (your own question)" },
  { value: "Notice", label: "Tell them something" },
];

/** A readable id for something new, unique among the ones already used. */
function newId(name: string, taken: Set<string>): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "item";
  let id = base;
  for (let n = 2; taken.has(id); n++) id = `${base}-${String(n)}`;
  return id;
}

function blankType(id: string, name: string): ExpenseTypeDefinition {
  return {
    id,
    name,
    active: true,
    isMeal: false,
    explanation: "Off",
    field: "None",
    fieldRequired: "Off",
    receipt: "Block",
    receiptAboveUsd: 0,
    dailyLimitUsd: null,
    dailyLimit: "Off",
    perPersonLimitUsd: null,
    perPersonLimit: "Off",
    maxPerDay: null,
    perDay: "Off",
    maxTipPercent: null,
    tip: "Off",
  };
}

/**
 * The expense categories and types people choose from, and the rules on each type: whether it is
 * checked, flagged for the approvers, or stops the report until it's fixed. Also the brands and
 * the currencies with their exchange rates. Everything is saved together.
 */
export function CategoriesPage() {
  const api = useApi();
  const { data, error, loading } = useApiQuery<Catalog>("/api/expenses/catalog");
  const [draft, setDraft] = useState<Catalog>();
  const [saved, setSaved] = useState<Catalog>();
  const [editing, setEditing] = useState<{ categoryId: string; typeId: string }>();
  const [result, setResult] = useState<{ ok: boolean; text: string }>();
  const [busy, setBusy] = useState(false);
  const [newBrand, setNewBrand] = useState("");

  if (loading && !data) {
    return (
      <div className="card" aria-busy="true">
        <div className="skeleton line" />
        <div className="skeleton line wide" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="error" role="alert">
        {error?.message ?? "Could not load the categories."}
      </div>
    );
  }

  const catalog = draft ?? saved ?? data;
  const dirty = draft !== undefined;
  const taken = new Set(catalog.categories.flatMap((c) => [c.id, ...c.types.map((t) => t.id)]));

  function change(next: Catalog) {
    setDraft(next);
    setResult(undefined);
  }

  function updateCategory(
    id: string,
    update: (c: ExpenseCategoryDefinition) => ExpenseCategoryDefinition,
  ) {
    change({
      ...catalog,
      categories: catalog.categories.map((c) => (c.id === id ? update(c) : c)),
    });
  }

  function updateType(categoryId: string, typeId: string, update: Partial<ExpenseTypeDefinition>) {
    updateCategory(categoryId, (c) => ({
      ...c,
      types: c.types.map((t) => (t.id === typeId ? { ...t, ...update } : t)),
    }));
  }

  function move(index: number, by: number) {
    const next = [...catalog.categories];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(index + by, 0, moved);
    change({ ...catalog, categories: next });
  }

  function save() {
    setBusy(true);
    setResult(undefined);
    api
      .put<Catalog>("/api/expenses/catalog", catalog)
      .then((stored) => {
        setSaved(stored);
        setDraft(undefined);
        setResult({ ok: true, text: "Saved. New expenses use these within a minute." });
      })
      .catch((caught: unknown) =>
        setResult({ ok: false, text: caught instanceof Error ? caught.message : String(caught) }),
      )
      .finally(() => setBusy(false));
  }

  const open = editing
    ? catalog.categories
        .find((c) => c.id === editing.categoryId)
        ?.types.find((t) => t.id === editing.typeId)
    : undefined;

  return (
    <div className="narrow">
      <Link className="back" to="/settings">
        <Icon name="chevronLeft" size={18} /> Settings
      </Link>
      <div className="page-head">
        <h1>Categories & rules</h1>
      </div>
      <p className="muted lead">
        What people choose when they add an expense, and the rules on each type. <b>Warn</b> flags
        the expense for the employee and approvers; <b>Enforce</b> stops the report until it's
        fixed.
      </p>

      {catalog.categories.map((category, index) => (
        <section
          key={category.id}
          className={`card stack category ${category.active ? "" : "retired"}`}
        >
          <div className="category-head">
            <input
              className="category-name"
              value={category.name}
              onChange={(e) => updateCategory(category.id, (c) => ({ ...c, name: e.target.value }))}
              aria-label="Category name"
            />
            <button
              type="button"
              className="icon-btn"
              disabled={index === 0}
              onClick={() => move(index, -1)}
              aria-label={`Move ${category.name} up`}
            >
              <Icon name="chevronUp" size={18} />
            </button>
            <button
              type="button"
              className="icon-btn"
              disabled={index === catalog.categories.length - 1}
              onClick={() => move(index, 1)}
              aria-label={`Move ${category.name} down`}
            >
              <Icon name="chevronDown" size={18} />
            </button>
          </div>
          <div className="split gl-fields">
            <label>
              GL report column
              <input
                value={category.reportColumn ?? ""}
                onChange={(e) =>
                  updateCategory(category.id, (c) => ({ ...c, reportColumn: e.target.value }))
                }
                placeholder={category.name}
              />
            </label>
            <label>
              GL code <span className="optional">optional</span>
              <input
                value={category.glCode ?? ""}
                onChange={(e) =>
                  updateCategory(category.id, (c) => ({ ...c, glCode: e.target.value || null }))
                }
                placeholder="30805.8560"
              />
            </label>
          </div>
          <label>
            Available to <span className="optional">optional</span>
            <input
              key={`${category.id}-${(category.availableTo ?? []).join(",")}`}
              defaultValue={(category.availableTo ?? []).join(", ")}
              onBlur={(e) =>
                updateCategory(category.id, (c) => ({
                  ...c,
                  availableTo: e.target.value
                    .split(/[,;\s]+/)
                    .map((who) => who.trim().toLowerCase())
                    .filter(Boolean),
                }))
              }
              placeholder="Everyone"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <p className="muted small">
            Leave empty for everyone. Type <strong>executives</strong> for the executive approvers
            in Settings, or sign-in names like todd@planetart.com. Others won&apos;t see it, and the
            AI won&apos;t pick it for them.
          </p>
          <ul className="type-list">
            {category.types.map((type) => (
              <li key={type.id}>
                <button
                  type="button"
                  className={`type-row ${type.active ? "" : "retired"}`}
                  onClick={() => setEditing({ categoryId: category.id, typeId: type.id })}
                >
                  <span>
                    <strong>{type.name}</strong>
                    <span className="muted small">{ruleSummary(type)}</span>
                  </span>
                  <Icon name="chevronRight" size={18} />
                </button>
              </li>
            ))}
          </ul>
          <div className="row">
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                const id = newId(`${category.id}-new-type`, taken);
                updateCategory(category.id, (c) => ({
                  ...c,
                  types: [...c.types, blankType(id, "New type")],
                }));
                setEditing({ categoryId: category.id, typeId: id });
              }}
            >
              <Icon name="plus" size={16} /> Add type
            </button>
            <label className="check">
              <input
                type="checkbox"
                checked={category.active}
                onChange={(e) =>
                  updateCategory(category.id, (c) => ({ ...c, active: e.target.checked }))
                }
              />
              In use
            </label>
          </div>
        </section>
      ))}

      <button
        type="button"
        className="btn secondary"
        onClick={() => {
          const id = newId("new-category", taken);
          const typeId = newId(`${id}-type`, taken);
          change({
            ...catalog,
            categories: [
              ...catalog.categories,
              { id, name: "New category", active: true, types: [blankType(typeId, "New type")] },
            ],
          });
        }}
      >
        <Icon name="plus" size={16} /> Add category
      </button>

      <section className="card stack">
        <h2>Brands</h2>
        <p className="muted">Offered on expense types that ask for a brand.</p>
        <ul className="chip-list">
          {catalog.brands.map((brand) => (
            <li key={brand} className="chip">
              {brand}
              <button
                type="button"
                className="link-btn"
                onClick={() =>
                  change({ ...catalog, brands: catalog.brands.filter((b) => b !== brand) })
                }
                aria-label={`Remove ${brand}`}
              >
                <Icon name="x" size={14} />
              </button>
            </li>
          ))}
        </ul>
        <div className="input-action">
          <input
            value={newBrand}
            onChange={(e) => setNewBrand(e.target.value)}
            placeholder="Add a brand"
            aria-label="New brand"
          />
          <button
            type="button"
            className="icon-btn"
            disabled={newBrand.trim() === ""}
            onClick={() => {
              change({ ...catalog, brands: [...catalog.brands, newBrand.trim()] });
              setNewBrand("");
            }}
            aria-label="Add brand"
          >
            <Icon name="plus" />
          </button>
        </div>
      </section>

      <CurrencyEditor
        currencies={catalog.currencies}
        onChange={(currencies) => change({ ...catalog, currencies })}
      />

      {result && (
        <div className={result.ok ? "success" : "error"} role="status">
          {result.text}
        </div>
      )}
      {dirty && (
        <div className="action-bar">
          <div className="action-total">
            <span className="muted">Unsaved changes</span>
          </div>
          {dirty && (
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              onClick={() => setDraft(undefined)}
            >
              Undo
            </button>
          )}
          <button type="button" className="btn" disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}

      <Sheet
        open={open !== undefined}
        title={open?.name ?? "Expense type"}
        onClose={() => setEditing(undefined)}
      >
        {open && editing && (
          <TypeEditor
            type={open}
            onChange={(update) => updateType(editing.categoryId, open.id, update)}
            onDone={() => setEditing(undefined)}
          />
        )}
      </Sheet>
    </div>
  );
}

/** The rules on a type in a few words, for the list. */
function ruleSummary(type: ExpenseTypeDefinition): string {
  const parts: string[] = [];
  if (!type.active) parts.push("retired");
  if (type.receipt !== "Off")
    parts.push(
      type.receiptAboveUsd > 0 ? `receipt from ${money(type.receiptAboveUsd)}` : "receipt",
    );
  if (type.explanation !== "Off") parts.push("explanation");
  if (type.field !== "None" && type.fieldRequired !== "Off")
    parts.push(
      type.field === "Notice"
        ? "a message"
        : type.field === "Other"
          ? (type.fieldPrompt ?? "a question")
          : (Fields.find((f) => f.value === type.field)?.label.toLowerCase() ?? ""),
    );
  if (type.dailyLimit !== "Off" && type.dailyLimitUsd !== null)
    parts.push(`${money(type.dailyLimitUsd)}/day`);
  if (type.perPersonLimit !== "Off" && type.perPersonLimitUsd !== null)
    parts.push(`${money(type.perPersonLimitUsd)}/person`);
  if (type.perDay !== "Off" && type.maxPerDay !== null)
    parts.push(`${String(type.maxPerDay)} a day`);
  if (type.tip !== "Off" && type.maxTipPercent !== null)
    parts.push(`tip ≤ ${String(type.maxTipPercent)}%`);
  return parts.length === 0 ? "No rules" : parts.join(" · ");
}

function ModePicker({
  value,
  onChange,
  label,
}: {
  value: RuleMode;
  onChange: (mode: RuleMode) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {Modes.map((mode) => (
        <button
          key={mode.value}
          type="button"
          role="radio"
          aria-checked={value === mode.value}
          className={`${value === mode.value ? "on" : ""} ${mode.value.toLowerCase()}`}
          onClick={() => onChange(mode.value)}
        >
          {mode.label}
        </button>
      ))}
    </div>
  );
}

/** A number field that's empty for "no limit". */
function LimitInput({
  value,
  onChange,
  prefix,
  suffix,
  label,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  prefix?: string;
  suffix?: string;
  label: string;
}) {
  return (
    <span className={prefix ? "input-prefix" : "input-suffix"}>
      {prefix && <span aria-hidden="true">{prefix}</span>}
      <input
        value={value === null ? "" : String(value)}
        onChange={(e) => {
          const text = e.target.value.trim();
          onChange(text === "" || Number.isNaN(Number(text)) ? null : Number(text));
        }}
        inputMode="decimal"
        placeholder="No limit"
        aria-label={label}
      />
      {suffix && <span aria-hidden="true">{suffix}</span>}
    </span>
  );
}

function Rule({
  title,
  hint,
  mode,
  onMode,
  children,
}: {
  title: string;
  hint?: string;
  mode: RuleMode;
  onMode: (mode: RuleMode) => void;
  children?: ReactNode;
}) {
  return (
    <div className="rule">
      <div className="rule-head">
        <span>
          <strong>{title}</strong>
          {hint && <span className="muted small">{hint}</span>}
        </span>
        <ModePicker value={mode} onChange={onMode} label={title} />
      </div>
      {children && mode !== "Off" && <div className="rule-value">{children}</div>}
    </div>
  );
}

function TypeEditor({
  type,
  onChange,
  onDone,
}: {
  type: ExpenseTypeDefinition;
  onChange: (update: Partial<ExpenseTypeDefinition>) => void;
  onDone: () => void;
}) {
  return (
    <div className="stack">
      <label>
        Name
        <input value={type.name} onChange={(e) => onChange({ name: e.target.value })} />
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={type.active}
          onChange={(e) => onChange({ active: e.target.checked })}
        />
        In use (retired types stay on old expenses but can't be chosen)
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={type.isMeal}
          onChange={(e) => onChange({ isMeal: e.target.checked })}
        />
        A meal: receipt reading decides between individual and team, and asks for the tip
      </label>

      <Rule title="Receipt" mode={type.receipt} onMode={(receipt) => onChange({ receipt })}>
        <label>
          Needed at or above (0 for always)
          <LimitInput
            prefix="$"
            value={type.receiptAboveUsd}
            onChange={(value) => onChange({ receiptAboveUsd: value ?? 0 })}
            label="Receipt needed at or above"
          />
        </label>
      </Rule>

      <Rule
        title="Explanation"
        hint="A reason written in the note"
        mode={type.explanation}
        onMode={(explanation) => onChange({ explanation })}
      />

      <div className="rule">
        <label>
          Also ask for or tell
          <select
            value={type.field}
            onChange={(e) => {
              const field = e.target.value as ExtraField;
              // A message set to Off would never show; start it at Warn.
              onChange(
                field === "Notice" && type.fieldRequired === "Off"
                  ? { field, fieldRequired: "Warn" }
                  : { field },
              );
            }}
          >
            {Fields.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
        {type.field === "Notice" && (
          <label>
            Message to show
            <input
              value={type.fieldPrompt ?? ""}
              onChange={(e) => onChange({ fieldPrompt: e.target.value })}
              placeholder="Airfare is booked through Travel Desk and isn't usually reimbursed."
            />
          </label>
        )}
        {type.field === "Other" && (
          <label>
            Question to ask
            <input
              value={type.fieldPrompt ?? ""}
              onChange={(e) => onChange({ fieldPrompt: e.target.value })}
              placeholder="Which project was this for?"
            />
          </label>
        )}
        {type.field !== "None" && (
          <div className="rule-head">
            <span>
              <strong>{type.field === "Notice" ? "Show it" : "Required"}</strong>
              <span className="muted small">
                {type.field === "Notice"
                  ? "Warn shows it in orange, Enforce in red"
                  : type.field === "Attendees"
                    ? "List of the other people"
                    : type.field === "Other"
                      ? "Must be answered"
                      : "Must be filled in"}
              </span>
            </span>
            <ModePicker
              value={type.fieldRequired}
              onChange={(fieldRequired) => onChange({ fieldRequired })}
              label="Required"
            />
          </div>
        )}
      </div>

      <Rule
        title="Daily allowance"
        hint="Per person, tax and tip included"
        mode={type.dailyLimit}
        onMode={(dailyLimit) => onChange({ dailyLimit })}
      >
        <LimitInput
          prefix="$"
          value={type.dailyLimitUsd}
          onChange={(dailyLimitUsd) => onChange({ dailyLimitUsd })}
          label="Daily allowance"
        />
      </Rule>

      <Rule
        title="Limit per person"
        hint="For shared meals, divided by the people there"
        mode={type.perPersonLimit}
        onMode={(perPersonLimit) => onChange({ perPersonLimit })}
      >
        <LimitInput
          prefix="$"
          value={type.perPersonLimitUsd}
          onChange={(perPersonLimitUsd) => onChange({ perPersonLimitUsd })}
          label="Limit per person"
        />
      </Rule>

      <Rule title="How many a day" mode={type.perDay} onMode={(perDay) => onChange({ perDay })}>
        <LimitInput
          value={type.maxPerDay}
          onChange={(value) => onChange({ maxPerDay: value === null ? null : Math.round(value) })}
          label="How many a day"
        />
      </Rule>

      <Rule
        title="Tip"
        hint="Percent of the bill before the tip"
        mode={type.tip}
        onMode={(tip) => onChange({ tip })}
      >
        <LimitInput
          suffix="%"
          value={type.maxTipPercent}
          onChange={(maxTipPercent) => onChange({ maxTipPercent })}
          label="Largest tip"
        />
      </Rule>

      <button type="button" className="btn" onClick={onDone}>
        Done
      </button>
      <p className="muted small">Changes are kept on the page until you press Save.</p>
    </div>
  );
}

function CurrencyEditor({
  currencies,
  onChange,
}: {
  currencies: CurrencyRate[];
  onChange: (currencies: CurrencyRate[]) => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");

  return (
    <section className="card stack">
      <h2>Currencies</h2>
      <p className="muted">
        Expenses can be entered in these. The amount is converted to US dollars at the rate here,
        and the employee can correct it.
      </p>
      {currencies.map((currency) => (
        <div key={currency.code} className="currency-row">
          <span>
            <strong>{currency.code}</strong>
            <span className="muted small">{currency.name}</span>
          </span>
          <label className="currency-rate">
            <span className="muted small">1 {currency.code} =</span>
            <LimitInput
              prefix="$"
              value={currency.usdPerUnit}
              onChange={(value) =>
                onChange(
                  currencies.map((c) =>
                    c.code === currency.code ? { ...c, usdPerUnit: value ?? 0 } : c,
                  ),
                )
              }
              label={`US dollars per ${currency.code}`}
            />
          </label>
          <button
            type="button"
            className="icon-btn"
            onClick={() => onChange(currencies.filter((c) => c.code !== currency.code))}
            aria-label={`Remove ${currency.code}`}
          >
            <Icon name="trash" size={18} />
          </button>
        </div>
      ))}
      <div className="currency-add">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 3))}
          placeholder="EUR"
          aria-label="Currency code"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Euro"
          aria-label="Currency name"
        />
        <input
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          inputMode="decimal"
          placeholder="USD each"
          aria-label="US dollars per unit"
        />
        <button
          type="button"
          className="icon-btn"
          disabled={code.length !== 3 || !(Number(rate) > 0)}
          onClick={() => {
            onChange([
              ...currencies,
              { code, name: name.trim() || code, usdPerUnit: Number(rate) },
            ]);
            setCode("");
            setName("");
            setRate("");
          }}
          aria-label="Add currency"
        >
          <Icon name="plus" />
        </button>
      </div>
    </section>
  );
}
